// Reporting API - Datasets, Categories, Dashboards, Indicators, Imports, Commodity Codes
// Standalone reporting module (decoupled from barratt)
import api, { type AxiosResponse, type PaginatedResponse } from './client';

// ============================================================================
// Types (re-exported from barratt for backward compatibility)
// ============================================================================

export interface Dataset {
  id: number;
  name: string;
  ds_type: 'invoice' | 'supplier_performance' | 'division_performance' | 'quality' | 'custom';
  ds_type_display?: string;
  data_source: 'upload' | 'api' | 'manual';
  data_source_display?: string;
  description?: string;
  is_enabled: boolean;
  company?: number;
  company_name?: string;
  owner?: number;
  owner_name?: string;
  icon_content?: string;
  icon_extension?: string;
  import_number?: number;
  last_data_update?: string;
  fields?: DatasetField[];
  record_count?: number;
  /** FieldRegistry DSID this dataset is backed by (0=Suppliers, 701=Contracts, etc.) */
  dsid?: number | null;
  /** Number of fields exposed by the FieldRegistry for this dataset's DSID. */
  available_field_count?: number;
  /** Number of saved Dashboards (queries/views) that use this dataset. */
  saved_query_count?: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface DatasetField {
  id: number;
  dataset: number;
  bind_field: string;
  title: string;
  field_type:
    | 'string'
    | 'integer'
    | 'decimal'
    | 'date'
    | 'datetime'
    | 'boolean'
    | 'lookup'
    | 'percentage'
    | 'currency'
    | 'time'
    | 'document'
    | 'link';
  field_type_display?: string;
  is_enabled: boolean;
  is_key_field: boolean;
  is_filter: boolean;
  filter_type: 'none' | 'dropdown' | 'search' | 'date_range' | 'number_range';
  filter_type_display?: string;
  is_default_view: boolean;
  is_sortable: boolean;
  align_type: 'left' | 'center' | 'right';
  use_thousand_separator: boolean;
  decimal_places: number;
  date_format: string;
  char_length?: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DatasetRecord {
  id: number;
  data: Record<string, unknown>;
  created_at: string;
}

export interface AnalyseCategory {
  id: number;
  name: string;
  ds_type: 'standard' | 'barratt';
  ds_type_display?: string;
  hint?: string;
  company?: number;
  sort_order: number;
  icon_content?: string;
  icon_extension?: string;
  icon_version?: number;
  original_category?: number;
  created_by?: number;
  created_by_name?: string;
  dashboard_count?: number;
  created_at: string;
  updated_at: string;
}

export interface AnalyseDashboard {
  id: number;
  name: string;
  description?: string;
  is_enabled: boolean;
  category?: number;
  category_name?: string;
  dataset?: number;
  dataset_name?: string;
  is_filter_board: boolean;
  is_advanced_filter_board: boolean;
  filter_field?: number;
  shared_with: 'private' | 'company' | 'company_and_suppliers';
  shared_with_display?: string;
  level: 1 | 2 | 3;
  level_display?: string;
  download_lock: boolean;
  company?: number;
  sort_order: number;
  app_code?: string;
  indicator_count?: number;
  icon_content?: string;
  icon_extension?: string;
  icon_version?: number;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardIndicator {
  id: number;
  category: number;
  category_name?: string;
  category_dashboard?: number;
  name: string;
  chart_type: 'bar' | 'line' | 'pie' | 'donut' | 'waterfall' | 'heatmap' | 'summary' | 'grid' | 'kpi' | 'indicator' | 'geolocation' | 'markup';
  chart_type_display?: string;
  hint?: string;
  config: Record<string, unknown>;
  dataset?: number;
  dataset_name?: string;
  shape_key?: number;
  icon_content?: string;
  icon_extension?: string;
  icon_version?: number;
  original_indicator_id?: number;
  original_dashboard_id?: number;
  dashboard?: number; // write-only: AnalyseDashboard ID for indicator creation
  company?: number;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
}

export interface CommodityCode {
  id: number;
  code: string;
  name: string;
  description?: string;
  parent?: number;
  parent_name?: string;
  level: number;
  children_count?: number;
  full_path?: string;
  company?: number;
  is_active: boolean;
  children?: CommodityCode[];
  created_at: string;
  updated_at: string;
}

export interface ImportSummary {
  id: number;
  dataset: number;
  dataset_name?: string;
  template?: number;
  template_display?: string;
  company?: number;
  company_name?: string;
  user?: number;
  user_name?: string;
  total_records: number;
  total_pages: number;
  imported_pages: number;
  imported_pages_success: number;
  page_size: number;
  is_success: boolean;
  is_complete: boolean;
  completed_by_manual: boolean;
  is_create_fields: boolean;
  summary_type: 1 | 14;
  summary_type_display?: string;
  progress_percentage?: number;
  log_count?: number;
  total_added?: number;
  total_failed?: number;
  has_errors?: boolean;
  created_at: string;
  completed_at?: string;
}

export interface ImportLog {
  id: number;
  import_summary: number;
  company?: number;
  company_name?: string;
  user?: number;
  user_name?: string;
  page_index: number;
  is_success: boolean;
  import_count: number;
  added: number;
  force_added: number;
  updated: number;
  skipped: number;
  failed: number;
  key_field_empty: number;
  repeat: number;
  deleted: number;
  empty_row: number;
  merge_field_not_found: number;
  calculated_failed: number;
  description?: string;
  push_time?: string;
  created_at: string;
  finish_time?: string;
  exception_count?: number;
  success_rate?: number;
  exceptions?: ImportException[];
}

export interface ImportException {
  id: number;
  import_log: number;
  row_number: number;
  column_name?: string;
  severity: 'error' | 'warning' | 'info';
  severity_display?: string;
  error_code?: string;
  message: string;
  raw_data: Record<string, string>;
  created_at: string;
}

// ============================================================================
// Builder catalog types (Report Builder UI)
// ============================================================================

export type FieldKind = 'group_key' | 'value' | 'date_bucket' | 'enum';
export type FieldDataType = 'string' | 'int' | 'decimal' | 'date' | 'datetime' | 'enum' | 'bool';
export type FieldFilterType =
  | 'none'
  | 'text'
  | 'multi_select'
  | 'date_range'
  | 'number_range'
  | 'multi_select_value';

export interface BuilderEnumChoice {
  value: string | number;
  label: string;
}

export interface BuilderFieldDef {
  dsid: number;
  field_id: number;
  label: string;
  short_label: string;
  kind: FieldKind;
  data_type: FieldDataType;
  filter_type: FieldFilterType;
  is_filterable?: boolean;
  enum_choices: BuilderEnumChoice[];
  drilldown_url?: string;
}

export interface BuilderDataset {
  dsid: number;
  label: string;
  fields: BuilderFieldDef[];
}

export interface DsidCatalogResponse {
  datasets: BuilderDataset[];
}

export interface OperatorEntry {
  key: string;
  label: string;
}

export interface OperatorCatalogResponse {
  text: OperatorEntry[];
  number: OperatorEntry[];
  enum: OperatorEntry[];
  bool: OperatorEntry[];
  date: OperatorEntry[];
  datetime: OperatorEntry[];
}

/**
 * Single builder filter row. `value` shape depends on the operator
 * — see `backend/reporting/filter_engine` for the canonical shape.
 */
export interface BuilderFilter {
  field_dsid: number;
  field_id: number;
  field_data_type?: FieldDataType;
  operator: string;
  value: Record<string, unknown>;
  date_from_today?: boolean;
  shifts?: string;
  sort_order?: number;
}

export interface BuilderFilterGroup {
  name?: string;
  group_op: 'AND' | 'OR';
  filters: BuilderFilter[];
  sort_order?: number;
}

export interface BuilderAxis {
  dsid: number;
  field_id: number;
  date_interval?: number | null;
  paging_count?: number | null;
}

export interface BuilderYAxis {
  dsid: number;
  field_id: number;
  stats_type: number;
}

export interface BuilderExtraYAxis {
  field_dsid: number;
  field_id: number;
  stats_type: number;
  show_type: number;
  title?: string;
}

/** Grid grouping & aggregate config (legacy Grid_Group / Grid_Total). */
export interface BuilderGridGroupBy {
  dsid: number;
  field_id: number;
}

export type BuilderGridAggOp = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'distinct_count';

export interface BuilderGridAggregate {
  column: string;
  op: BuilderGridAggOp;
}

export interface BuilderGridSortItem {
  column: string;
  dir: 'asc' | 'desc';
}

/**
 * Conditional formatting rule — row colouring based on a column comparison.
 * Mirrors the legacy `_FormatNotes.cshtml` format-rule editor.
 */
export type FormatRuleOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains';

export interface BuilderFormatRule {
  column: string;
  op: FormatRuleOp;
  value: string | number;
  color: string; // CSS colour (#rgb / #rrggbb / rgb(…))
  background?: string;
}

export interface IndicatorBuilderConfig {
  x?: BuilderAxis | null;
  y?: BuilderYAxis | null;
  x2?: BuilderAxis | null;
  target_value?: number | null;
  show_sma?: boolean;
  sma_period?: number | null;
  show_label?: boolean;
  show_total?: boolean;
  x_title?: string;
  y_title?: string;
  paging_count?: number | null;
  description?: string;
  filters?: BuilderFilter[];
  filter_groups?: BuilderFilterGroup[];
  extra_y_axes?: BuilderExtraYAxis[];
  // Grid-specific (legacy Grid_Group / Grid_Total / Grid_Orderby)
  columns?: Array<{ dsid: number; field_id: number }>;
  group_by?: BuilderGridGroupBy | null;
  aggregates?: BuilderGridAggregate[];
  sort_list?: BuilderGridSortItem[];
  format_rules?: BuilderFormatRule[];
  [extraKey: string]: unknown;
}

export interface IndicatorPreviewRequest {
  config: IndicatorBuilderConfig;
  chart_type?: string;
}

export interface IndicatorPreviewResponse {
  indicator_id?: number;
  indicator_name?: string;
  chart_type: string;
  data?: Record<string, unknown>[];
  series?: Record<string, { label: string; stats_type?: number; show_type?: number }>;
  meta?: Record<string, unknown>;
  summary?: Record<string, unknown> | null;
  message?: string;
  [extraKey: string]: unknown;
}

// ============================================================================
// Reports v2 — Sharing, Scheduling, Filters
// ============================================================================

export interface ReportShare {
  id: number;
  dashboard: number;
  target_type: 'user' | 'team' | 'company' | 'role';
  target_type_display?: string;
  target_id: number;
  can_edit: boolean;
  created_by?: number;
  created_at: string;
}

export interface DashboardPublishToken {
  id: number;
  dashboard: number;
  token: string;
  label: string;
  created_by?: number | null;
  created_by_name?: string;
  created_at: string;
  expires_at: string | null;
  is_active: boolean;
  last_accessed_at: string | null;
  access_count: number;
  is_valid: boolean;
}

export interface FiscalCalendar {
  id: number;
  company: number;
  company_name?: string;
  start_month: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardDownloadLogEntry {
  id: number;
  dashboard: number;
  dashboard_name: string;
  user: number | null;
  user_name: string;
  user_email: string;
  company: number | null;
  company_name: string;
  format: 'xlsx' | 'pdf' | 'csv' | 'png';
  source: 'manual' | 'scheduled';
  byte_size: number;
  downloaded_at: string;
}

export interface ReportRevisionEntry {
  id: number;
  dashboard: number;
  indicator: number | null;
  config_snapshot: Record<string, unknown>;
  note: string;
  changed_by: number | null;
  changed_by_name: string;
  changed_at: string;
}

export interface DashboardPublishTokenCreatePayload {
  dashboard: number;
  label?: string;
  expires_at?: string | null;
  is_active?: boolean;
}

export interface ScheduledReportRecipient {
  id?: number;
  schedule?: number;
  email: string;
  name?: string;
}

export type ScheduleFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly';
export type ScheduleFormat = 'excel' | 'pdf' | 'png' | 'csv';

export interface ScheduledReport {
  id: number;
  dashboard: number;
  frequency: ScheduleFrequency;
  frequency_display?: string;
  day_of_week?: number | null;
  day_of_month?: number | null;
  hour: number;
  timezone: string;
  formats: ScheduleFormat[];
  last_run_at?: string | null;
  next_run_at?: string | null;
  is_active: boolean;
  created_by?: number;
  created_at: string;
  updated_at: string;
  recipients: ScheduledReportRecipient[];
}

export interface ReportFilterValue {
  value?: string | number | boolean | null;
  value_end?: string | number | null;
  n?: number;
  separator?: string;
  values?: Array<string | number>;
  start?: string | number | null;
  end?: string | number | null;
  [extraKey: string]: unknown;
}

export interface ReportFilter {
  id: number;
  indicator: number;
  group?: number | null;
  field_dsid: number;
  field_id: number;
  field_data_type: number;
  operator: string;
  value: ReportFilterValue;
  date_from_today: boolean;
  shifts: string;
  sort_order: number;
  created_at?: string;
}

export interface ReportTemplate {
  id: number;
  name: string;
  description: string;
  scope: 'builtin' | 'company' | 'user';
  scope_display?: string;
  chart_type: string;
  chart_type_display?: string;
  config: Record<string, unknown>;
  icon_name: string;
  company: number | null;
  created_by: number | null;
  created_by_name?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ReportTemplateCreatePayload {
  name: string;
  description?: string;
  scope: 'company' | 'user';
  chart_type: string;
  config: Record<string, unknown>;
  icon_name?: string;
  sort_order?: number;
}

export type ReportFilterCreatePayload = Omit<ReportFilter, 'id' | 'created_at'>;
export type ReportShareCreatePayload = Omit<ReportShare, 'id' | 'created_at' | 'created_by' | 'target_type_display'>;
export type ScheduledReportCreatePayload = Omit<
  ScheduledReport,
  'id' | 'created_at' | 'updated_at' | 'last_run_at' | 'next_run_at' | 'created_by' | 'frequency_display'
>;

// ============================================================================
// API Methods
// ============================================================================

export const reportingAPI = {
  // ============================
  // Datasets
  // ============================
  getDatasets: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<Dataset>>> =>
    api.get('/reporting/datasets/', { params }),

  getDataset: (id: number): Promise<AxiosResponse<Dataset>> =>
    api.get(`/reporting/datasets/${id}/`),

  createDataset: (data: Partial<Dataset>): Promise<AxiosResponse<Dataset>> =>
    api.post('/reporting/datasets/', data),

  updateDataset: (id: number, data: Partial<Dataset>): Promise<AxiosResponse<Dataset>> =>
    api.patch(`/reporting/datasets/${id}/`, data),

  deleteDataset: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/datasets/${id}/`),

  getDatasetFields: (datasetId: number): Promise<AxiosResponse<DatasetField[]>> =>
    api.get(`/reporting/datasets/${datasetId}/fields/`),

  addDatasetField: (datasetId: number, data: Partial<DatasetField>): Promise<AxiosResponse<DatasetField>> =>
    api.post(`/reporting/datasets/${datasetId}/add_field/`, data),

  getDatasetRecords: (datasetId: number, params?: { page?: number; page_size?: number }): Promise<AxiosResponse<{
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    results: DatasetRecord[];
  }>> =>
    api.get(`/reporting/datasets/${datasetId}/records/`, { params }),

  // ============================
  // Analyse Categories
  // ============================
  getCategories: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<AnalyseCategory>>> =>
    api.get('/reporting/categories/', { params }),

  getCategory: (id: number): Promise<AxiosResponse<AnalyseCategory>> =>
    api.get(`/reporting/categories/${id}/`),

  createCategory: (data: { name: string; hint?: string; sort_order?: number }): Promise<AxiosResponse<AnalyseCategory>> =>
    api.post('/reporting/categories/', data),

  updateCategory: (id: number, data: Partial<AnalyseCategory>): Promise<AxiosResponse<AnalyseCategory>> =>
    api.patch(`/reporting/categories/${id}/`, data),

  deleteCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/categories/${id}/`),

  renameCategory: (id: number, name: string): Promise<AxiosResponse<AnalyseCategory>> =>
    api.post(`/reporting/categories/${id}/rename/`, { name }),

  // ============================
  // Analyse Dashboards
  // ============================
  getDashboards: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<AnalyseDashboard>>> =>
    api.get('/reporting/dashboards/', { params }),

  getDashboard: (id: number): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.get(`/reporting/dashboards/${id}/`),

  createDashboard: (data: Partial<AnalyseDashboard>): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.post('/reporting/dashboards/', data),

  updateDashboard: (id: number, data: Partial<AnalyseDashboard>): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.patch(`/reporting/dashboards/${id}/`, data),

  deleteDashboard: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/dashboards/${id}/`),

  shareDashboard: (id: number, sharedWith: string): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.post(`/reporting/dashboards/${id}/share/`, { shared_with: sharedWith }),

  // ============================
  // Dashboard Indicators
  // ============================
  getIndicators: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DashboardIndicator>>> =>
    api.get('/reporting/indicators/', { params }),

  getIndicator: (id: number): Promise<AxiosResponse<DashboardIndicator>> =>
    api.get(`/reporting/indicators/${id}/`),

  createIndicator: (data: Partial<DashboardIndicator>): Promise<AxiosResponse<DashboardIndicator>> =>
    api.post('/reporting/indicators/', data),

  updateIndicator: (id: number, data: Partial<DashboardIndicator>): Promise<AxiosResponse<DashboardIndicator>> =>
    api.patch(`/reporting/indicators/${id}/`, data),

  deleteIndicator: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/indicators/${id}/`),

  // ============================
  // Commodity Codes
  // ============================
  getCommodityCodes: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<CommodityCode>>> =>
    api.get('/reporting/commodity-codes/', { params }),

  getCommodityCode: (id: number): Promise<AxiosResponse<CommodityCode>> =>
    api.get(`/reporting/commodity-codes/${id}/`),

  createCommodityCode: (data: Partial<CommodityCode>): Promise<AxiosResponse<CommodityCode>> =>
    api.post('/reporting/commodity-codes/', data),

  updateCommodityCode: (id: number, data: Partial<CommodityCode>): Promise<AxiosResponse<CommodityCode>> =>
    api.patch(`/reporting/commodity-codes/${id}/`, data),

  deleteCommodityCode: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/commodity-codes/${id}/`),

  getCommodityCodeTree: (): Promise<AxiosResponse<CommodityCode[]>> =>
    api.get('/reporting/commodity-codes/tree/'),

  getCommodityCodeChildren: (id: number): Promise<AxiosResponse<CommodityCode[]>> =>
    api.get(`/reporting/commodity-codes/${id}/children/`),

  // ============================
  // Import Summaries
  // ============================
  getImportSummaries: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<ImportSummary>>> =>
    api.get('/reporting/import-summaries/', { params }),

  getImportSummary: (id: number): Promise<AxiosResponse<ImportSummary>> =>
    api.get(`/reporting/import-summaries/${id}/`),

  // ============================
  // Builder catalog & preview
  // ============================
  getDsidCatalog: (): Promise<AxiosResponse<DsidCatalogResponse>> =>
    api.get('/reporting/builder/dsid-catalog/'),

  getOperatorCatalog: (): Promise<AxiosResponse<OperatorCatalogResponse>> =>
    api.get('/reporting/builder/operator-catalog/'),

  /**
   * Preview an indicator's chart payload without persisting any config.
   * The backend resolves `config` (live from the builder) using the same
   * resolver pipeline used for saved indicators, and returns the chart
   * envelope for client-side rendering.
   */
  previewIndicator: (
    id: number,
    payload: IndicatorPreviewRequest,
  ): Promise<AxiosResponse<IndicatorPreviewResponse>> =>
    api.post(`/reporting/indicators/${id}/preview/`, payload),

  // ============================
  // Reports v2 — Duplicate / Share / Schedule / Filters
  // ============================
  duplicateDashboard: (id: number, name?: string): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.post(`/reporting/dashboards/${id}/duplicate/`, name ? { name } : {}),

  duplicateIndicator: (id: number, name?: string): Promise<AxiosResponse<DashboardIndicator>> =>
    api.post(`/reporting/indicators/${id}/duplicate/`, name ? { name } : {}),

  markDashboardSaved: (id: number): Promise<AxiosResponse<{ is_saved: boolean }>> =>
    api.post(`/reporting/dashboards/${id}/mark_saved/`, {}),

  // Per-user shares
  listShares: (dashboardId: number): Promise<AxiosResponse<PaginatedResponse<ReportShare>>> =>
    api.get('/reporting/shares/', { params: { dashboard: dashboardId } }),

  createShare: (payload: ReportShareCreatePayload): Promise<AxiosResponse<ReportShare>> =>
    api.post('/reporting/shares/', payload),

  updateShare: (id: number, payload: Partial<ReportShareCreatePayload>): Promise<AxiosResponse<ReportShare>> =>
    api.patch(`/reporting/shares/${id}/`, payload),

  deleteShare: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/shares/${id}/`),

  // Scheduled reports
  listSchedules: (dashboardId: number): Promise<AxiosResponse<PaginatedResponse<ScheduledReport>>> =>
    api.get('/reporting/schedules/', { params: { dashboard: dashboardId } }),

  createSchedule: (payload: ScheduledReportCreatePayload): Promise<AxiosResponse<ScheduledReport>> =>
    api.post('/reporting/schedules/', payload),

  updateSchedule: (id: number, payload: Partial<ScheduledReportCreatePayload>): Promise<AxiosResponse<ScheduledReport>> =>
    api.patch(`/reporting/schedules/${id}/`, payload),

  deleteSchedule: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/schedules/${id}/`),

  // Publish tokens (legacy view type 8)
  listPublishTokens: (dashboardId: number): Promise<AxiosResponse<PaginatedResponse<DashboardPublishToken>>> =>
    api.get('/reporting/publish-tokens/', { params: { dashboard: dashboardId } }),

  createPublishToken: (payload: DashboardPublishTokenCreatePayload): Promise<AxiosResponse<DashboardPublishToken>> =>
    api.post('/reporting/publish-tokens/', payload),

  updatePublishToken: (
    id: number,
    payload: Partial<DashboardPublishTokenCreatePayload>,
  ): Promise<AxiosResponse<DashboardPublishToken>> =>
    api.patch(`/reporting/publish-tokens/${id}/`, payload),

  deletePublishToken: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/publish-tokens/${id}/`),

  /**
   * Public-facing URL for a published token. Points at the frontend
   * `/public/dashboard/:token` route which renders the resolved indicators
   * via chart components — the JSON API endpoint
   * (`/api/v1/reporting/publish/<token>/`) is what that page fetches under
   * the hood, so recipients see a real dashboard rather than raw JSON.
   */
  publishedDashboardUrl: (token: string): string =>
    `/public/dashboard/${token}`,

  // Admin surfaces — fiscal calendar / download audit / revision history
  listFiscalCalendars: (): Promise<AxiosResponse<PaginatedResponse<FiscalCalendar>>> =>
    api.get('/reporting/fiscal-calendars/'),

  createFiscalCalendar: (payload: Partial<FiscalCalendar>): Promise<AxiosResponse<FiscalCalendar>> =>
    api.post('/reporting/fiscal-calendars/', payload),

  updateFiscalCalendar: (id: number, payload: Partial<FiscalCalendar>): Promise<AxiosResponse<FiscalCalendar>> =>
    api.patch(`/reporting/fiscal-calendars/${id}/`, payload),

  deleteFiscalCalendar: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/fiscal-calendars/${id}/`),

  listDownloadLogs: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DashboardDownloadLogEntry>>> =>
    api.get('/reporting/download-logs/', { params }),

  listRevisions: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<ReportRevisionEntry>>> =>
    api.get('/reporting/revisions/', { params }),

  // Report templates — saved cfg snapshots used when creating new charts
  listTemplates: (): Promise<AxiosResponse<PaginatedResponse<ReportTemplate>>> =>
    api.get('/reporting/templates/'),

  createTemplate: (payload: ReportTemplateCreatePayload): Promise<AxiosResponse<ReportTemplate>> =>
    api.post('/reporting/templates/', payload),

  updateTemplate: (
    id: number,
    payload: Partial<ReportTemplateCreatePayload>,
  ): Promise<AxiosResponse<ReportTemplate>> =>
    api.patch(`/reporting/templates/${id}/`, payload),

  deleteTemplate: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/templates/${id}/`),

  // Indicator filters (CRUD)
  listFilters: (indicatorId: number): Promise<AxiosResponse<PaginatedResponse<ReportFilter>>> =>
    api.get('/reporting/filters/', { params: { indicator: indicatorId } }),

  createFilter: (payload: ReportFilterCreatePayload): Promise<AxiosResponse<ReportFilter>> =>
    api.post('/reporting/filters/', payload),

  updateFilter: (id: number, payload: Partial<ReportFilterCreatePayload>): Promise<AxiosResponse<ReportFilter>> =>
    api.patch(`/reporting/filters/${id}/`, payload),

  deleteFilter: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/reporting/filters/${id}/`),

  /**
   * Build the download URL for a dashboard export.
   *
   * Uses `file_format` (not `format`) because DRF reserves `format` as a
   * renderer selector via `URL_FORMAT_OVERRIDE` — sending `?format=xlsx`
   * would trigger DRF's content negotiation and 404 before the view runs.
   */
  exportDashboardUrl: (id: number, format: 'csv' | 'xlsx' | 'pdf' | 'png'): string => {
    const params = new URLSearchParams();
    params.set('file_format', format);
    return `/api/v1/reporting/dashboards/${id}/export/?${params.toString()}`;
  },

  /**
   * Fetch a dashboard export via axios (carries the auth header + axios
   * baseURL) and trigger a browser download. Use this instead of navigating
   * to `exportDashboardUrl` directly — plain navigation omits the auth token
   * and resolves the URL against the SPA origin, so the browser lands on the
   * React fallback page instead of downloading.
   */
  downloadDashboardExport: async (
    id: number,
    format: 'csv' | 'xlsx' | 'pdf' | 'png',
    indicatorIds?: number[],
  ): Promise<void> => {
    const params: Record<string, string> = { file_format: format };
    if (indicatorIds && indicatorIds.length > 0) {
      params['indicator_ids'] = indicatorIds.join(',');
    }
    const res = await api.get(`/reporting/dashboards/${id}/export/`, {
      params,
      responseType: 'blob',
    });
    const blob = res.data as Blob;
    const disposition = (res.headers['content-disposition'] as string | undefined) || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] || `dashboard-${id}.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

export default reportingAPI;
